import { addDays, differenceInCalendarDays, isSameDay, isToday, isYesterday, parseISO, setHours, startOfDay } from 'date-fns';
import type { Locale } from '@nexops/shared';
import type { ChatAttachmentDto, ChatConversationDto, ChatUser } from '@nexops/shared';
import type { MessageKey } from '@/i18n/I18nProvider';
import { formatDate } from '@/shared/lib/format';
import type { ChatMessageItem, MuteOption } from '../types';

type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

export const fullName = (u: Pick<ChatUser, 'firstName' | 'lastName'>): string => `${u.firstName} ${u.lastName}`.trim();

export function isMuted(c: Pick<ChatConversationDto, 'mutedUntil'>, now = Date.now()): boolean {
  return !!c.mutedUntil && Date.parse(c.mutedUntil) > now;
}

/** Druga osoba w rozmowie 1:1. */
export function otherParticipant(c: ChatConversationDto, meId: string): ChatUser | undefined {
  return c.participants.find((p) => p.user.id !== meId)?.user;
}

export function titleInitials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

/** ISO do `mutedUntil`; `null` zdejmuje wyciszenie. */
export function muteUntilIso(option: MuteOption, now = new Date()): string | null {
  switch (option) {
    case '1h':
      return new Date(now.getTime() + 3_600_000).toISOString();
    case '8h':
      return new Date(now.getTime() + 8 * 3_600_000).toISOString();
    case 'tomorrow':
      return setHours(startOfDay(addDays(now, 1)), 8).toISOString();
    default:
      return null;
  }
}

/** Polskie formy liczby mnogiej (one / few / many); angielski mapuje wszystko poza „1” na `many`. */
export function pluralForm(locale: Locale, n: number): 'one' | 'few' | 'many' {
  const cat = new Intl.PluralRules(locale === 'pl' ? 'pl-PL' : 'en-GB').select(n);
  if (cat === 'one') return 'one';
  if (cat === 'few') return 'few';
  return 'many';
}

export function formatClock(iso: string, locale: Locale): string {
  return formatDate(iso, locale, 'HH:mm');
}

/** Czas w liście rozmów: dziś — godzina, wczoraj, ten tydzień — dzień tygodnia, dalej — data. */
export function formatListTime(iso: string, locale: Locale, t: Translate): string {
  const d = parseISO(iso);
  if (isToday(d)) return formatDate(iso, locale, 'HH:mm');
  if (isYesterday(d)) return t('chat.time.yesterday');
  if (differenceInCalendarDays(new Date(), d) < 7) return formatDate(iso, locale, 'EEE');
  return formatDate(iso, locale, 'd MMM');
}

export function dayLabel(iso: string, locale: Locale, t: Translate): string {
  const d = parseISO(iso);
  if (isToday(d)) return t('chat.time.today');
  if (isYesterday(d)) return t('chat.time.yesterday');
  return formatDate(iso, locale, 'EEEE, d MMMM yyyy');
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;
export const isImageAttachment = (a: Pick<ChatAttachmentDto, 'mime' | 'name'>): boolean =>
  (a.mime ? a.mime.startsWith('image/') : false) || IMAGE_EXT.test(a.name);

export const fileExtension = (name: string): string => name.split('.').pop()?.toLowerCase() ?? '';

/** Wiersz wątku: separator dnia, znacznik „nowe wiadomości”, wiadomość systemowa albo zwykła. */
export type ThreadRow =
  | { type: 'day'; key: string; date: string }
  | { type: 'unread'; key: string }
  | { type: 'system'; key: string; message: ChatMessageItem }
  | { type: 'message'; key: string; message: ChatMessageItem; showSender: boolean; groupStart: boolean; groupEnd: boolean };

const GROUP_GAP_MS = 5 * 60_000;

interface GroupOptions {
  meId: string;
  /** Moment ostatniego odczytu przy otwarciu rozmowy (null = nigdy); `undefined` = bez znacznika. */
  unreadAfter?: string | null;
  /** W rozmowie 1:1 nie pokazujemy nazwy nadawcy. */
  showSenders: boolean;
}

/** Układa wiadomości w wiersze: separatory dni, grupowanie po nadawcy, znacznik nowych wiadomości. */
export function buildThreadRows(items: ChatMessageItem[], { meId, unreadAfter, showSenders }: GroupOptions): ThreadRow[] {
  const rows: ThreadRow[] = [];
  let dividerPlaced = unreadAfter === undefined;
  let prev: ChatMessageItem | null = null;
  let prevRow: Extract<ThreadRow, { type: 'message' }> | null = null;

  for (const message of items) {
    const created = parseISO(message.createdAt);
    const newDay = !prev || !isSameDay(parseISO(prev.createdAt), created);
    if (newDay) {
      rows.push({ type: 'day', key: `day-${message.id}`, date: message.createdAt });
      prev = null;
      prevRow = null;
    }
    if (!dividerPlaced && message.sender.id !== meId && message.kind !== 'SYSTEM' && !message.local) {
      if (unreadAfter === null || (unreadAfter !== undefined && Date.parse(message.createdAt) > Date.parse(unreadAfter))) {
        rows.push({ type: 'unread', key: 'unread' });
        dividerPlaced = true;
        prev = null;
        prevRow = null;
      }
    }

    if (message.kind === 'SYSTEM') {
      rows.push({ type: 'system', key: message.id, message });
      prev = null;
      prevRow = null;
      continue;
    }

    const continues =
      !!prev &&
      prev.sender.id === message.sender.id &&
      created.getTime() - parseISO(prev.createdAt).getTime() < GROUP_GAP_MS;
    const row: Extract<ThreadRow, { type: 'message' }> = {
      type: 'message',
      key: message.id,
      message,
      showSender: showSenders && message.sender.id !== meId && !continues,
      groupStart: !continues,
      groupEnd: true,
    };
    if (continues && prevRow) prevRow.groupEnd = false;
    rows.push(row);
    prev = message;
    prevRow = row;
  }
  return rows;
}

const URL_RE = /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]])/g;

/** Dzieli tekst na fragmenty zwykłe i adresy URL (do linkowania w dymku). */
export function splitLinks(text: string): Array<{ text: string; url?: string }> {
  const parts: Array<{ text: string; url?: string }> = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push({ text: text.slice(last, idx) });
    parts.push({ text: m[0], url: m[0] });
    last = idx + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}
